import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, API, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, FileText, Upload, Landmark, IdCard, Wallet } from "lucide-react";

const DOC_FIELDS = [
  { kind: "passport", label: "Passport photo", numKey: "passport_no", numLabel: "Passport number" },
  { kind: "aadhar", label: "Aadhaar card", numKey: "aadhar_no", numLabel: "Aadhaar number" },
  { kind: "pan", label: "PAN card", numKey: "pan_no", numLabel: "PAN number" },
];

export function OwnerProfileButton({ o, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const docRefs = useRef({});
  const docFor = useRef(null);

  const openDialog = () => {
    setForm({
      name: o.name || "", phone: o.phone || "", details: o.details || "",
      passport_no: o.passport_no || "", aadhar_no: o.aadhar_no || "", pan_no: o.pan_no || "",
      bank_name: o.bank_name || "", account_number: o.account_number || "", ifsc: o.ifsc || "", upi_id: o.upi_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/owners/${o.id}/profile`, form);
      toast.success("Company partner profile updated");
      setOpen(false); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadDoc = async (file) => {
    const kind = docFor.current;
    if (!file || !kind) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/owners/${o.id}/document?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "aadhar" ? "Aadhaar" : kind.toUpperCase()} uploaded`);
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const field = (key, label, mono = false) => (
    <div><Label className="text-xs text-muted-foreground">{label}</Label>
      <Input data-testid={`owner-profile-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={`mt-1 bg-card/60 ${mono ? "font-mono" : ""}`} /></div>
  );

  return (
    <>
      <Button size="sm" variant="outline" data-testid={`owner-profile-edit-${o.id}`} onClick={openDialog}
        className="mt-2 w-full border-gold/40 text-gold text-[10px] h-7 gap-1"><Pencil className="w-3 h-3" /> Edit profile, KYC &amp; bank</Button>
      <input type="file" accept="image/*,.pdf" className="hidden" data-testid={`owner-doc-input-${o.id}`}
        ref={(el) => (docRefs.current.main = el)} onChange={(e) => uploadDoc(e.target.files?.[0])} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#05261D] border-gold/20 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2"><IdCard className="w-5 h-5" /> {o.name} — Profile &amp; KYC</DialogTitle>
            <DialogDescription>Company partners can edit everything here except the profit share percentage (admin only).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {field("name", "Name")}
            <div className="grid grid-cols-2 gap-3">
              {field("phone", "Mobile")}
              <div><Label className="text-xs text-muted-foreground">Profit share (admin only)</Label>
                <Input value={`${o.percentage}%`} readOnly disabled className="mt-1 bg-card/40 text-gold font-mono" data-testid="owner-profile-pct-readonly" /></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Details / role</Label>
              <Textarea data-testid="owner-profile-details" value={form.details || ""} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1 bg-card/60 min-h-[60px]" /></div>

            <p className="eyebrow text-gold pt-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> KYC Documents</p>
            {DOC_FIELDS.map((d) => (
              <div key={d.kind} className="flex items-end gap-2">
                <div className="flex-1">{field(d.numKey, d.numLabel, true)}</div>
                <Button type="button" variant="outline" size="sm" data-testid={`owner-doc-upload-${d.kind}`}
                  onClick={() => { docFor.current = d.kind; docRefs.current.main?.click(); }}
                  className="h-9 border-gold/40 text-gold text-[10px] gap-1 flex-shrink-0"><Upload className="w-3 h-3" /> {d.label}</Button>
                {o[`${d.kind}_doc`] && (
                  <a data-testid={`owner-doc-view-${d.kind}`} href={`${API}/owners/${o.id}/document/${d.kind}?auth=${token}`} target="_blank" rel="noopener noreferrer"
                    className="h-9 inline-flex items-center px-2 rounded-md border border-emerald-400/40 text-emerald-300 text-[10px] flex-shrink-0">View</a>
                )}
              </div>
            ))}

            <p className="eyebrow text-gold pt-2 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Bank — for profit-share payout</p>
            {field("bank_name", "Bank name")}
            <div className="grid grid-cols-2 gap-3">
              {field("account_number", "Account number", true)}
              {field("ifsc", "IFSC", true)}
            </div>
            {field("upi_id", "UPI ID", true)}
          </div>
          <DialogFooter>
            <Button data-testid="owner-profile-save" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Profile"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OwnerLedger({ o }) {
  const [data, setData] = useState({ entries: [], total_payout: 0, total_expense: 0 });
  const [form, setForm] = useState({ kind: "payout", label: "", amount: "", note: "" });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => setData((await api.get(`/owners/${o.id}/ledger`)).data), [o.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label.trim() || !(parseFloat(form.amount) > 0)) { toast.error("Label and amount required"); return; }
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (editId) await api.put(`/owner-ledger/${editId}`, body);
      else await api.post(`/owners/${o.id}/ledger`, body);
      toast.success(editId ? "Entry updated" : "Entry added");
      setOpen(false); setEditId(null); setForm({ kind: "payout", label: "", amount: "", note: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/owner-ledger/${id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="mt-3 p-3 bg-card/30 border-gold/20" data-testid={`owner-ledger-${o.id}`}>
      <div className="flex items-center justify-between">
        <p className="eyebrow text-gold flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> {o.name}'s ledger</p>
        <Button size="sm" variant="ghost" data-testid={`ledger-add-${o.id}`}
          onClick={() => { setEditId(null); setForm({ kind: "payout", label: "", amount: "", note: "" }); setOpen(true); }}
          className="h-6 px-2 text-gold hover:bg-gold/10 text-[10px] gap-1"><Plus className="w-3 h-3" /> Add</Button>
      </div>
      <div className="flex gap-3 mt-1.5 text-[10px]">
        <span className="text-muted-foreground">Paid out: <span className="text-gold font-mono">{inr(data.total_payout)}</span></span>
        <span className="text-muted-foreground">Expenses paid: <span className="text-emerald-300 font-mono">{inr(data.total_expense)}</span></span>
      </div>
      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
        {data.entries.length === 0 && <p className="text-[11px] text-muted-foreground">No entries yet.</p>}
        {data.entries.map((e) => (
          <div key={e.id} data-testid={`ledger-row-${e.id}`} className="flex items-center gap-2 border-t border-border/40 pt-1.5 text-[11px]">
            <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${e.kind === "payout" ? "border-gold/50 text-gold" : "border-emerald-400/50 text-emerald-300"}`}>{e.kind}</Badge>
            <span className="flex-1 min-w-0 truncate text-white">{e.label}{e.note ? ` — ${e.note}` : ""}</span>
            <span className="font-mono text-gold flex-shrink-0">{inr(e.amount)}</span>
            <button type="button" data-testid={`ledger-edit-${e.id}`} title="Edit"
              onClick={() => { setEditId(e.id); setForm({ kind: e.kind, label: e.label, amount: e.amount, note: e.note || "" }); setOpen(true); }}
              className="text-gold hover:bg-gold/10 rounded p-0.5"><Pencil className="w-3 h-3" /></button>
            <button type="button" data-testid={`ledger-del-${e.id}`} title="Delete" onClick={() => del(e.id)}
              className="text-destructive hover:bg-destructive/10 rounded p-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#05261D] border-gold/20 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-gold">{editId ? "Edit entry" : `Add entry — ${o.name}`}</DialogTitle>
            <DialogDescription>Expenses also count in the company P&amp;L.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Type</Label>
              <select data-testid="ledger-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} disabled={!!editId}
                className="w-full h-9 mt-1 rounded-md bg-card/60 border border-input px-2 text-sm text-white">
                <option value="payout">Payout to company partner (profit share paid)</option>
                <option value="expense">Expense paid by company partner (counts in P&amp;L)</option>
              </select></div>
            <div><Label className="text-xs">Label</Label><Input data-testid="ledger-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. October profit share" /></div>
            <div><Label className="text-xs">Amount (₹)</Label><Input data-testid="ledger-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-card/60 font-mono" /></div>
            <div><Label className="text-xs">Note (optional)</Label><Input data-testid="ledger-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-card/60" /></div>
          </div>
          <DialogFooter><Button data-testid="ledger-save" onClick={save} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{editId ? "Save changes" : "Add entry"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
